import { useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useEconomyStore } from '@/state/useEconomyStore'
import { playerWorldPos } from '@/state/playerPosition'
import type { VillagePlacement } from '@/world/villages/types'
import { STRUCTURE_INFO } from '@/world/villages/types'
import { sampleTerrainRadius } from '@/world/TerrainSphere'
import { VILLAGE_PROFILES } from '@/economy/types'

// Shared across all villages — set by VillageRenderer
let _villagePlacements: VillagePlacement[] = []
let _dockPlacements: VillagePlacement[] = []

export function setVillagePlacements(villages: VillagePlacement[], docks: VillagePlacement[]) {
  _villagePlacements = villages
  _dockPlacements = docks
}

export function getVillagePlacements(): VillagePlacement[] {
  return _villagePlacements
}

export function getDockPlacements(): VillagePlacement[] {
  return _dockPlacements
}

/** Compute the world position at the end of a dock's pier (where ships should spawn).
 *  Replicates the same pier-length probe logic as buildDock. */
export function getDockPierEndPosition(dockIndex: number): THREE.Vector3 | null {
  if (dockIndex < 0 || dockIndex >= _dockPlacements.length) return null
  const dock = _dockPlacements[dockIndex]
  const structure = dock.layout.structures[0]
  if (!structure) return null

  const info = STRUCTURE_INFO.dock
  const hd = info.depth / 2

  // Reconstruct the building basis (same as buildDock)
  const worldOffset = new THREE.Vector3()
    .addScaledVector(dock.tangentX, structure.localX)
    .addScaledVector(dock.tangentZ, structure.localZ)
  const dir = dock.position.clone().add(worldOffset).normalize()
  const r = sampleTerrainRadius(dir.x, dir.y, dir.z)
  const pos = dir.multiplyScalar(r)
  const normal = pos.clone().normalize()

  // Orientation: same as buildingBasis
  let upVec = normal.clone()
  const arb = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
  let rightVec = new THREE.Vector3().crossVectors(upVec, arb).normalize()
  let fwd = new THREE.Vector3().crossVectors(rightVec, upVec).normalize()

  const cosY = Math.cos(structure.rotation)
  const sinY = Math.sin(structure.rotation)
  const newRight = rightVec.clone().multiplyScalar(cosY).add(fwd.clone().multiplyScalar(sinY))
  const newFwd = fwd.clone().multiplyScalar(cosY).sub(rightVec.clone().multiplyScalar(sinY))
  rightVec = newRight.normalize()
  fwd = newFwd.normalize()

  // Probe forward to find ocean (same constants as buildDock)
  const OCEAN_EXTEND = 40
  const SAMPLE_STEP = 2
  const MAX_PROBE = 80
  const OCEAN_R = 10000
  const OCEAN_EPSILON = 0.5
  let distToOcean = MAX_PROBE
  for (let probe = 0; probe < MAX_PROBE; probe += SAMPLE_STEP) {
    const samplePos = pos.clone().addScaledVector(fwd, hd + 1 + probe)
    const sDir = samplePos.clone().normalize()
    const sR = sampleTerrainRadius(sDir.x, sDir.y, sDir.z)
    if (sR <= OCEAN_R + OCEAN_EPSILON) {
      distToOcean = probe
      break
    }
  }

  const pierLength = distToOcean + OCEAN_EXTEND
  const pierStart = hd + 1.0
  // Position at the end of the pier, slightly past the mooring posts
  const pierEndPos = pos.clone()
    .addScaledVector(fwd, pierStart + pierLength + 5)
  // Project onto sphere at ocean radius
  const pierEndDir = pierEndPos.clone().normalize()
  return pierEndDir.multiplyScalar(OCEAN_R)
}

const INTERACTION_RANGE = 15
const CHECK_INTERVAL = 0.15 // seconds between proximity checks (not every frame)

const _structureWorldPos = new THREE.Vector3()

export const STRUCTURE_SUBTITLES: Record<string, string> = {
  castle: 'Village Keep',
  house: "Villager's Home",
  trading_post: 'Village Market',
  church: 'Chapel',
  pub: 'Tavern',
  armory: 'Smithy',
  stable: 'Stable',
  barn: 'Storehouse',
  dock: 'Harbor',
  farm: 'Farmstead',
  pasture: 'Paddock',
}

/** Get structure world position from village placement + local coords */
function getStructureWorldPos(village: VillagePlacement, localX: number, localZ: number): THREE.Vector3 {
  // Project local XZ offset onto sphere surface via tangent vectors
  _structureWorldPos.copy(village.position)
    .addScaledVector(village.tangentX, localX)
    .addScaledVector(village.tangentZ, localZ)
  // Re-project onto sphere (normalize direction, set to terrain radius)
  const len = _structureWorldPos.length()
  if (len > 0) _structureWorldPos.multiplyScalar(village.position.length() / len)
  return _structureWorldPos
}

export function InteractionDetector() {
  const checkTimer = useRef(0)
  const setNearestTarget = useEconomyStore((s) => s.setNearestTarget)
  const openPanel = useEconomyStore((s) => s.openPanel)
  const closePanel = useEconomyStore((s) => s.closePanel)
  const panelOpen = useEconomyStore((s) => s.panelOpen)

  // E key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'e') {
        const store = useEconomyStore.getState()
        if (store.panelOpen) {
          closePanel()
        } else if (store.nearestTarget) {
          openPanel()
        }
      }
      if (e.key === 'Escape') {
        const store = useEconomyStore.getState()
        if (store.panelOpen) {
          closePanel()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [openPanel, closePanel])

  useFrame((_, delta) => {
    checkTimer.current += delta
    if (checkTimer.current < CHECK_INTERVAL) return
    checkTimer.current = 0

    // Don't update nearest while panel is open
    if (panelOpen) return

    const playerPos = playerWorldPos
    if (playerPos.lengthSq() === 0) {
      setNearestTarget(null)
      return
    }

    let bestDist = Infinity
    let bestTarget: Parameters<typeof setNearestTarget>[0] = null

    // Check village structures
    for (let vi = 0; vi < _villagePlacements.length; vi++) {
      const village = _villagePlacements[vi]

      // Coarse check: distance to village center
      const villageDist = playerPos.distanceTo(village.position)
      const maxReach = (village.layout.fenceRadius || 200) + 100
      if (villageDist > maxReach) continue

      // Check each structure
      for (let si = 0; si < village.layout.structures.length; si++) {
        const structure = village.layout.structures[si]
        const worldPos = getStructureWorldPos(village, structure.localX, structure.localZ)
        const dist = playerPos.distanceTo(worldPos)

        if (dist < INTERACTION_RANGE && dist < bestDist) {
          bestDist = dist
          const profileInfo = vi < VILLAGE_PROFILES.length ? VILLAGE_PROFILES[vi] : null
          bestTarget = {
            villageIndex: vi,
            structureIndex: si,
            structureType: structure.type,
            distance: dist,
            villageName: profileInfo
              ? `Village #${vi}`
              : `Settlement #${vi}`,
            serverId: profileInfo?.serverId ?? null,
            profileType: profileInfo?.profile ?? null,
          }
        }
      }
    }

    // Check dock structures (they're separate placements, each with 1 structure)
    for (let di = 0; di < _dockPlacements.length; di++) {
      const dock = _dockPlacements[di]
      const dockDist = playerPos.distanceTo(dock.position)
      if (dockDist > 50) continue

      if (dockDist < INTERACTION_RANGE && dockDist < bestDist) {
        bestDist = dockDist
        bestTarget = {
          villageIndex: -1, // docks aren't villages
          structureIndex: 0,
          structureType: 'dock',
          distance: dockDist,
          villageName: `Dock #${di}`,
          serverId: null,
          profileType: null,
        }
      }
    }

    setNearestTarget(bestTarget)
  })

  return null
}
